// Google reCAPTCHA v3 utility functions
// Handles loading reCAPTCHA script and generating tokens

declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

// reCAPTCHA configuration
const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || '';
const RECAPTCHA_SCRIPT_URL = RECAPTCHA_SITE_KEY 
  ? `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`
  : 'https://www.google.com/recaptcha/api.js';

// Track if script is loaded
let scriptLoaded = false;
let scriptLoading = false;

/**
 * Load reCAPTCHA script if not already loaded
 */
export const loadRecaptchaScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    // If already loaded, resolve immediately
    if (scriptLoaded && window.grecaptcha) {
      resolve();
      return;
    }

    // If currently loading, wait for it
    if (scriptLoading) {
      const checkLoaded = () => {
        if (scriptLoaded && window.grecaptcha) {
          resolve();
        } else {
          setTimeout(checkLoaded, 100);
        }
      };
      checkLoaded();
      return;
    }

    // Start loading
    scriptLoading = true;

    // Check if script already exists in DOM
    const existingScript = document.querySelector(`script[src="${RECAPTCHA_SCRIPT_URL}"]`);
    if (existingScript) {
      scriptLoading = false;
      scriptLoaded = true;
      resolve();
      return;
    }

    // Create and load script
    const script = document.createElement('script');
    script.src = RECAPTCHA_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      scriptLoaded = true;
      scriptLoading = false;
      resolve();
    };
    
    script.onerror = () => {
      scriptLoading = false;
      reject(new Error('Failed to load reCAPTCHA script'));
    };

    document.head.appendChild(script);
  });
};

/**
 * Generate reCAPTCHA token for a specific action
 * @param action - The action name (e.g., 'login', 'signup')
 * @returns Promise<string> - The reCAPTCHA token
 */
export const generateRecaptchaToken = async (action: string): Promise<string> => {
  try {
    // Ensure script is loaded
    await loadRecaptchaScript();

    if (!RECAPTCHA_SITE_KEY) {
      throw new Error('reCAPTCHA site key not configured');
    }

    // Generate token using reCAPTCHA v3
    return new Promise((resolve, reject) => {
      window.grecaptcha.ready(() => {
        window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action })
          .then((token: string) => {
            resolve(token);
          })
          .catch((error: any) => {
            reject(new Error(`reCAPTCHA token generation failed: ${error.message}`));
          });
      });
    });
  } catch (error) {
    throw new Error(`reCAPTCHA initialization failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Verify reCAPTCHA token on the backend
 * @param token - The reCAPTCHA token to verify
 * @param action - The expected action
 * @returns Promise<boolean> - Whether verification was successful
 */
export const verifyRecaptchaToken = async (token: string, action?: string): Promise<boolean> => {
  try {
    const response = await fetch('/api/auth/verify-recaptcha', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        token,
        action,
      }),
    });

    if (!response.ok) {
      throw new Error(`Verification request failed: ${response.status}`);
    }

    const result = await response.json();
    return result.success === true;
  } catch (error) {
    console.error('reCAPTCHA verification failed:', error);
    return false;
  }
};

/**
 * Check if reCAPTCHA is properly configured
 */
export const isRecaptchaConfigured = (): boolean => {
  return !!RECAPTCHA_SITE_KEY;
};

/**
 * Get reCAPTCHA configuration status for debugging
 */
export const getRecaptchaStatus = () => {
  return {
    siteKey: RECAPTCHA_SITE_KEY ? '✅ Configured' : '❌ Missing',
    scriptLoaded: scriptLoaded ? '✅ Loaded' : '❌ Not loaded',
    grecaptchaAvailable: typeof window !== 'undefined' && !!window.grecaptcha ? '✅ Available' : '❌ Not available',
  };
};
